import { NgClass } from "@angular/common";
import { Component, OnInit, WritableSignal, inject, signal, ChangeDetectionStrategy } from "@angular/core";
import { RouterModule } from "@angular/router";
import { first } from "rxjs";
import { ButtonComponent } from "../../shared/components/button/button.component";
import { SvgIconComponent } from "../../shared/components/svg-icon/svg-icon.component";
import { DialogModule } from "@openng/optimus-ui/dialog";
import { ButtonModule } from "@openng/optimus-ui/button";
import { InputTextModule } from "@openng/optimus-ui/inputtext";

import { DialogService, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { TenantFormComponent } from "./components/tenant-form/tenant-form.component";
import { UserFormComponent } from "./components/user-form/user-form.component";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { TenantAdministrationService } from "../../services/api/tenant-administration/tenant-administration.service";

import { ConfirmationService, MessageService } from "@openng/optimus-ui/api";

import { UserService } from "../../services/api/user/user.service";
import { CoreFormComponent } from "./components/core-form/core-form.component";
import { BotevoCoresService } from "../../services/api/botevo-cores/botevo-cores.service";
import { ControllerCatalogItemComponent } from "./components/controller-catalog-item/controller-catalog-item.component";
import { ControllerCatalogService } from "../../services/api/controller-catalog/controller-catalog.service";
import { CONTROLLER_CATALOG_ITEM_TYPES } from "../../enums/controllerCatalogItemTypes";
import { TooltipModule } from "@openng/optimus-ui/tooltip";
import { HasPermissionDirective } from "../../shared/directives/has-permission/has-permission.directive";
import { PermissionService } from "../../services/api/permission/permission.service";
import { createTimeoutSignal } from "../../shared/utils/timeout.utils";
import { TimeoutComponent } from "../../shared/components/timeout/timeout.component";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { SimplebarAngularModule } from "simplebar-angular";
import { Tenant } from "../../models/api/tenant";
import { User } from "../../models/api/user";
import { Reference } from "../../models/shared/refs";
import { AuthService } from "../../services/api/auth/auth.service";
import { USER_ROLES } from "../../enums/userRoles.enum";

@Component({
  standalone: true,
  selector: "app-tenant-administration",
  imports: [
    NgClass,
    RouterModule,
    SvgIconComponent,
    ButtonComponent,
    DialogModule,
    ButtonModule,
    InputTextModule,
    TranslateModule,
    ButtonModule,
    TooltipModule,
    HasPermissionDirective,
    TimeoutComponent,
    ProgressSpinnerModule,
    SimplebarAngularModule,
  ],
  templateUrl: "./tenant-administration.component.html",
  styleUrl: "./tenant-administration.component.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [],
})
export class TenantAdministrationComponent implements OnInit {
  public tenants: WritableSignal<Tenant[] | null> = signal(null);
  public selectedTenantDetails: WritableSignal<Tenant | null> = signal(null);
  public translate = inject(TranslateService);
  private controllerCatalogService = inject(ControllerCatalogService);

  private tenantAdminService = inject(TenantAdministrationService);
  private confirmationService = inject(ConfirmationService);
  private coreService = inject(BotevoCoresService);
  private message = inject(MessageService);
  private dialogService = inject(DialogService);
  private userService = inject(UserService);
  private authService = inject(AuthService);

  public permissions = inject(PermissionService);
  private ref: DynamicDialogRef | null = null;

  public loading = signal(true);
  public timeOut = createTimeoutSignal(this.loading);

  /**
   * Tenant migrations may only be initiated by system administrators.
   */
  public get canOverrideModel(): boolean {
    return this.authService.getRole() === USER_ROLES.SYS_ADMIN;
  }

  ngOnInit(): void {
    this.fetchTenants();
  }

  /**
   * Fetches the list of tenants from the tenant administration service and updates the component's
   * tenants property with the received data.
   *
   * @return {void} No return value.
   */
  private fetchTenants(): void {
    this.loading.set(true);
    this.tenantAdminService
      .getTenants(true)
      .pipe(first())
      .subscribe({
        next: (tenants) => {
          this.loading.set(false);
          this.tenants.set(tenants);
          if (this.selectedTenantDetails() !== null) {
            this.refreshTenantDetails();
          }
        },
      });
  }

  /**
   * Selects a tenant and updates the component's selectedTenantDetails property.
   * @param tenant The tenant to select.
   */
  public onClickTenant(tenant: Tenant) {
    this.selectedTenantDetails.set(tenant);
  }

  /**
   * Refreshes the selected tenant details from the current tenant list.
   * This is necessary because the tenant list is updated in the background.
   */
  private refreshTenantDetails() {
    const updatedTenantDetails: Tenant | undefined = this.tenants()?.find((tenant) => {
      if (tenant.id === this.selectedTenantDetails()?.id) {
        return tenant;
      } else {
        return undefined;
      }
    });
    if (updatedTenantDetails) {
      this.selectedTenantDetails.set(updatedTenantDetails);
    }
  }

  /**
   * Opens a dialog to add a new tenant.
   */
  public openAddTenant(): void {
    const emptyTenant = new Tenant({ label: "", address: { country: "DE" } });
    this.ref = this.dialogService.open(TenantFormComponent, {
      header: this.translate.instant("admin.tenant_administration.tenant_form.add_tenant_header"),
      modal: true,
      width: "50vw",
      contentStyle: { overflow: "auto", padding: "20px" },
      breakpoints: {
        "960px": "75vw",
        "640px": "90vw",
      },
      data: { tenant: emptyTenant },
      closable: true,
    });
    this.ref?.onClose.subscribe({
      next: (tenant: Tenant | undefined) => {
        if (tenant) {
          this.tenantAdminService
            .createTenant(tenant)
            .pipe(first())
            .subscribe(this.afterServerCommunication("admin.tenant_administration.add_tenant_success"));
        }
      },
    });
  }

  /**
   * Opens a dialog to edit a tenant.
   *
   * @param {Event} event - The event that triggered the function.
   * @param {Tenant} tenant - The tenant to edit.
   */
  public openEditTenant(event: Event, tenant: Tenant): void {
    event.stopPropagation();
    this.ref = this.dialogService.open(TenantFormComponent, {
      header: this.translate.instant("admin.tenant_administration.tenant_form.edit_tenant_header"),
      modal: true,
      width: "50vw",
      contentStyle: { overflow: "auto", padding: "20px" },
      breakpoints: {
        "960px": "75vw",
        "640px": "90vw",
      },
      data: { tenant: tenant },
      closable: true,
    });
    this.ref?.onClose.subscribe({
      next: (tenant: Tenant | undefined) => {
        if (tenant) {
          this.tenantAdminService
            .editTenant(tenant)
            .pipe(first())
            .subscribe(this.afterServerCommunication("admin.tenant_administration.add_tenant_success"));
        }
      },
    });
  }

  /**
   * Opens a dialog to add a new user.
   */
  public openAddUser(): void {
    this.ref = this.dialogService.open(UserFormComponent, {
      header: this.translate.instant("admin.tenant_administration.user_form.add_user_header"),
      modal: true,
      width: "50vw",
      contentStyle: { overflow: "auto", padding: "20px" },
      breakpoints: {
        "960px": "75vw",
        "640px": "90vw",
      },
      data: {
        formFields: {},
        actionType: "add",
        tenantId: this.selectedTenantDetails()?.id?.toString(),
      },
      closable: true,
    });
    this.ref?.onClose.subscribe({
      next: (user: User | undefined) => {
        if (user && this.selectedTenantDetails) {
          this.userService
            .addUserFromTenant(user, this.selectedTenantDetails()?.id)
            .pipe(first())
            .subscribe(this.afterServerCommunication("admin.tenant_administration.edit_tenant_success"));
        }
      },
    });
  }

  /**
   * Opens a dialog to edit an existing user.
   * @param userId The ID of the user to edit.
   */
  public openEditUser(userId: string) {
    this.userService
      .getUserDetails(userId)
      .pipe(first())
      .subscribe((user) => {
        this.ref = this.dialogService.open(UserFormComponent, {
          header: this.translate.instant("admin.tenant_administration.user_form.edit_user_header"),
          modal: true,
          width: "50vw",
          contentStyle: { overflow: "auto", padding: "20px" },
          breakpoints: {
            "960px": "75vw",
            "640px": "90vw",
          },
          data: {
            formFields: user,
            actionType: "edit",
            tenantId: this.selectedTenantDetails()?.id?.toString(),
          },
          closable: true,
        });
        this.ref?.onClose.subscribe({
          next: (editedUser: User | undefined) => {
            if (editedUser) {
              editedUser.id = userId;
              editedUser.twoFactorEnabled = user.twoFactorEnabled;
              this.userService
                .editUser(editedUser)
                .subscribe(this.afterServerCommunication("admin.tenant_administration.edit_tenant_success"));
            }
          },
        });
      });
  }
  /**
   * Opens a dialog to add a new controller model.
   */
  public openAddModel(): void {
    this.controllerCatalogService
      .getCatalogItems(CONTROLLER_CATALOG_ITEM_TYPES.MODEL)
      .pipe(first())
      .subscribe({
        next: (models) => {
          this.ref = this.dialogService.open(ControllerCatalogItemComponent, {
            header: this.translate.instant("admin.tenant_administration.controller_catalog_assign.add_model"),
            modal: true,
            width: "50vw",
            contentStyle: { overflow: "auto", padding: "20px" },
            breakpoints: {
              "960px": "75vw",
              "640px": "90vw",
            },
            data: {
              catalogItemOptions: models,
              fieldTitle: this.translate.instant("admin.tenant_administration.controller_catalog_assign.models"),
            },
            closable: true,
          });
          this.ref?.onClose.subscribe({
            next: (selectedModel) => {
              if (selectedModel) {
                this.setControllerCatalogItems(undefined, selectedModel);
              }
            },
          });
        },
      });
  }

  /**
   * Opens a dialog to add a new controller manufacturer.
   */
  public openAddManufacturer(): void {
    this.controllerCatalogService
      .getCatalogItems(CONTROLLER_CATALOG_ITEM_TYPES.MANUFACTURER)
      .pipe(first())
      .subscribe({
        next: (manufacturers) => {
          this.ref = this.dialogService.open(ControllerCatalogItemComponent, {
            header: this.translate.instant("admin.tenant_administration.controller_catalog_assign.add_manufacturer"),
            modal: true,
            width: "50vw",
            contentStyle: { overflow: "auto", padding: "20px" },
            breakpoints: {
              "960px": "75vw",
              "640px": "90vw",
            },
            data: {
              catalogItemOptions: manufacturers,
              fieldTitle: this.translate.instant("admin.tenant_administration.controller_catalog_assign.manufacturers"),
            },
            closable: true,
          });
          this.ref?.onClose.subscribe({
            next: (selectedManufacturer) => {
              if (selectedManufacturer) {
                this.setControllerCatalogItems(selectedManufacturer, undefined);
              }
            },
          });
        },
      });
  }

  /**
   * Sets the controller catalog items on the currently selected tenant and
   * sends a request to the server to update the tenant.
   *
   * @param addedManufacturer - The newly added manufacturer reference.
   * @param addedModel - The newly added model reference.
   */
  private setControllerCatalogItems(addedManufacturer?: Reference, addedModel?: Reference) {
    if (addedModel) {
      this.selectedTenantDetails.update((tenantDetails) => {
        // creating a new reference with the added model to avoid mutation
        return tenantDetails
          ? new Tenant({
              ...tenantDetails,
              models: [...(tenantDetails.models ?? []), addedModel],
            })
          : tenantDetails;
      });
    }

    if (addedManufacturer) {
      this.selectedTenantDetails.update((tenantDetails) => {
        // creating a new reference with the added model to avoid mutation
        return tenantDetails
          ? new Tenant({
              ...tenantDetails,
              manufacturers: [...(tenantDetails.manufacturers ?? []), addedManufacturer],
            })
          : tenantDetails;
      });
    }
    if (this.selectedTenantDetails() && this.selectedTenantDetails()?.id) {
      this.tenantAdminService
        .editTenant(this.selectedTenantDetails()!)
        .pipe(first())
        .subscribe(this.afterServerCommunication("admin.tenant_administration.edit_tenant_success"));
    }
  }

  /**
   * Overwrites the model of the currently selected tenant with the given model ID.
   * This also sends a request to the server to update the tenant.
   * @param modelId - The ID of the model to overwrite with.
   */
  public overrideModel(modelId: string | undefined) {
    if (this.canOverrideModel && modelId && this.selectedTenantDetails()?.id) {
      const models = [modelId];
      this.tenantAdminService
        .overrideModel(models, this.selectedTenantDetails()!.id!)
        .pipe(first())
        .subscribe(this.afterServerCommunication("admin.tenant_administration.override_model_success"));
    }
  }

  /**
   * Deletes a model from the selected tenant's model list by the given model ID.
   * Updates the tenant details on the server after removing the model.
   * @param modelId - The ID of the model to be deleted.
   */
  public deleteModel(modelId: string | undefined) {
    if (modelId) {
      const models = this.selectedTenantDetails()?.models?.filter((model) => model.id !== modelId);
      if (this.selectedTenantDetails() && this.selectedTenantDetails()?.id) {
        this.selectedTenantDetails.update((tenantDetails) => {
          if (tenantDetails) {
            tenantDetails.models = models;
          }

          return tenantDetails;
        });
        this.tenantAdminService
          .editTenant(this.selectedTenantDetails()!)
          .pipe(first())
          .subscribe(this.afterServerCommunication("admin.tenant_administration.delete_model_success"));
      }
    }
  }

  /**
   * Deletes a manufacturer from the selected tenant's manufacturer list by the given manufacturer ID.
   * Updates the tenant details on the server after removing the manufacturer.
   * @param manufacturerId - The ID of the manufacturer to be deleted.
   */
  public deleteManufacturer(manufacturerId: string | undefined) {
    if (manufacturerId) {
      const manufacturers = (this.selectedTenantDetails()?.manufacturers as Reference[])?.filter(
        (manufacturer) => manufacturer.id !== manufacturerId
      );

      if (this.selectedTenantDetails() && this.selectedTenantDetails()?.id) {
        this.selectedTenantDetails.update((tenantDetails) => {
          if (tenantDetails) {
            tenantDetails.manufacturers = manufacturers;
          }
          return tenantDetails;
        });
        this.tenantAdminService
          .editTenant(this.selectedTenantDetails()!)
          .pipe(first())
          .subscribe(this.afterServerCommunication("admin.tenant_administration.delete_manufacturer_success"));
      }
    }
  }

  /**
   * Opens a confirmation popup to delete a tenant.
   *
   * @param {Event} event - The event that triggered the function.
   * @param {string | undefined} tenantId - The ID of the tenant to be deleted.
   * @return {void} No return value
   */
  public openConfirmPopupTenant(event: Event, tenantId: string | undefined) {
    event.stopPropagation();
    this.confirmationService.confirm({
      target: event.currentTarget as EventTarget,
      message: this.translate.instant("admin.tenant_administration.delete_confirm_tenant"),
      key: "global",
      accept: () => {
        this.tenantAdminService
          .deleteTenant(tenantId)
          .pipe(first())
          .subscribe(
            this.afterServerCommunication("admin.tenant_administration.delete_tenant_success", () => {
              this.selectedTenantDetails.set(null);
            })
          );
      },
    });
  }
  /**
   * Opens a confirmation popup to delete a user.
   *
   * @param {Event} event - The event that triggered the function.
   * @param {string} userId - The ID of the user to be deleted.
   * @return {void} No return value
   */
  public openConfirmPopupUser(event: Event, userId: string) {
    event.stopPropagation();
    this.confirmationService.confirm({
      target: event.currentTarget as EventTarget,
      message: this.translate.instant("admin.tenant_administration.delete_confirm_user"),
      key: "global",
      accept: () => {
        this.userService
          .deleteUser(userId)
          .pipe(first())
          .subscribe(this.afterServerCommunication("admin.tenant_administration.delete_user_success"));
      },
    });
  }

  /**
   * Opens a confirmation popup to delete a link between a tenant and a core.
   *
   * @param {Event} event - The event that triggered the function.
   * @param {string | undefined} coreId - The ID of the core to be unlinked.
   * @return {void} No return value
   */
  public openConfirmPopupCore(event: Event, coreId: string | undefined) {
    event.stopPropagation();
    this.confirmationService.confirm({
      target: event.currentTarget as EventTarget,
      message: this.translate.instant("admin.tenant_administration.delete_confirm_unlink"),
      key: "global",
      accept: () => {
        this.coreService
          .removeLinkCore(coreId, this.selectedTenantDetails()?.id)
          .pipe(first())
          .subscribe(this.afterServerCommunication("admin.tenant_administration.unlink_core_success"));
      },
    });
  }

  /**
   * Opens a dialog to add a new core to the tenant.
   *
   * A {@link CoreFormComponent} dialog is opened with a list of available cores.
   * When the dialog is closed with a core ID, the service is called to link the core to the tenant.
   * A success message is shown and the list of cores is refreshed.
   * @return {void} No return value
   */
  public openAddCore() {
    this.ref = this.dialogService.open(CoreFormComponent, {
      header: this.translate.instant("admin.tenant_administration.add_core"),
      modal: true,
      width: "50vw",
      contentStyle: { overflow: "auto", padding: "20px" },
      breakpoints: {
        "960px": "75vw",
        "640px": "90vw",
      },
      closable: true,
    });

    this.ref?.onClose.subscribe({
      next: (coreId: string) => {
        if (coreId) {
          this.coreService
            .linkCore(coreId, this.selectedTenantDetails()?.id)
            .pipe(first())
            .subscribe(this.afterServerCommunication("admin.tenant_administration.add_core_success"));
        }
      },
    });
  }

  /**
   * Handles server communication callbacks with optional success message.
   * @param successMessage Optional success message to display on successful operation.
   * @returns An object containing `next` and `error` callbacks.
   * - `next`: Displays a success message and refreshes the tenant list.
   * - `error`: Displays an error message with server error details.
   */
  private afterServerCommunication(successMessage?: string, cleanup?: () => void) {
    return {
      next: () => {
        if (successMessage) {
          this.message.add({
            key: "global",
            severity: "success",
            summary: this.translate.instant("common.success"),
            detail: this.translate.instant(successMessage),
            life: 4000,
          });
        }
        if (cleanup) cleanup();
        this.fetchTenants();
      },
      error: () => {
        this.fetchTenants();
      },
    };
  }
}
